import React, { createContext, useEffect, useState, ReactNode, FC, useContext, useRef } from 'react';
import { useToast } from './Toast';
import { getCompanyId } from '../services/company/company';
import { getUserById } from '../services/user/user';
import { browserSession } from '../services/utils/browserSession';
import LocaleSync from '../i18n/LocaleSync';
import { clearInsightsStandardCache } from '../services/statistics/insightsStandardCache';
import { clearHappinessReportFeedbacksStandardCache } from '../services/statistics/happinessReportFeedbacksStandardCache';
import { i18n } from '../i18n';

interface AuthContextType {
	user: {
		user: any;
		company_visited: any;
		verifyToken: any;
		confirmationCode: any;
	};
	saveDataSignUpOnStorage: any;
	updateCompanyVisited: any;
	SignOut: any;
	signOutSoft: any;
	saveDataVerifyOnStorage: any;
	saveRetryingFlagOnStorage: any;
	deleteRetryingFlagOnStorage: any;
	updateCurrentUser: (partialUser: Record<string, any>) => void;
	refreshCurrentUser: (userId?: string) => Promise<any | null>;
	isHydrating: boolean;
}

const initialUserState = {
	user: {},
	company_visited: null,
	verifyToken: null,
	confirmationCode: null,
};

const clearTemporaryStatisticsCaches = () => {
	clearInsightsStandardCache();
	clearHappinessReportFeedbacksStandardCache();
};

export const AuthContext = createContext<AuthContextType>({
	user: initialUserState,
	saveDataSignUpOnStorage: () => {},
	updateCompanyVisited: () => {},
	SignOut: () => {},
	signOutSoft: () => {},
	saveDataVerifyOnStorage: () => {},
	saveRetryingFlagOnStorage: () => {},
	deleteRetryingFlagOnStorage: () => {},
	updateCurrentUser: () => {},
	refreshCurrentUser: async () => null,
	isHydrating: true,
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [user, setUser] = useState(initialUserState);
	const [isHydrating, setIsHydrating] = useState(true);
	const refreshInFlight = useRef<{
		userId: string;
		promise: Promise<any | null>;
	} | null>(null);
	const sessionInvalidated = useRef(false);
	const { showToast } = useToast();

	const sanitizeUser = (userData: any) => {
		if (!userData) return {};

		const sanitized = { ...userData };
		delete sanitized.company_id;
		delete sanitized.company_name;
		delete sanitized.company;
		delete sanitized.company_visited;
		return sanitized;
	};

	const normalizeCompanyVisited = (response: any) => {
		return response?.company_visited ?? null;
	};

	const normalizeUserCompany = (response: any, fallback: any = null) => {
		return response?.user_company ??
			response?.company?.Item ??
			response?.company ??
			fallback ??
			null;
	};

	const enrichUserFromApi = (currentUser: any, userFromApi: any) => ({
		...currentUser,
		...sanitizeUser(userFromApi),
		group: userFromApi.group ?? currentUser.group ?? [],
		responsible_groups: userFromApi.responsible_groups ?? currentUser.responsible_groups ?? [],
		user_company:
			userFromApi.company?.Item ??
			userFromApi.company ??
			currentUser.user_company ??
			null,
		...(userFromApi.locale !== undefined ? { locale: userFromApi.locale } : {}),
	});

	const isUserActive = (status: unknown) => [1, 2].includes(Number(status));

	const getSessionLogoutDestination = () =>
		typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')
			? '/admin/signup'
			: '/signup';

	const persistIdentity = (userData: any, companyVisited: any) => {
		if (sessionInvalidated.current) return;

		browserSession.setIdentity({
			user: sanitizeUser(userData ?? {}),
			company_visited: companyVisited ?? null,
		});
	};

	const refreshUserFromApi = async (userId: string, currentUser: any, currentCompany: any) => {
		if (refreshInFlight.current?.userId === userId) {
			return refreshInFlight.current.promise;
		}

		const refreshPromise = (async () => {
			const userFromApi = await getUserById(userId);

			if (!userFromApi) return null;

			if (!isUserActive(userFromApi.status)) {
				sessionInvalidated.current = true;
				SignOut(getSessionLogoutDestination(), {
					message: String(i18n.t('errors.inactiveSessionClosing', { ns: 'auth' })),
					redirectDelay: 5000,
					toastDuration: 5000,
				});
				return null;
			}

			const latestIdentity = browserSession.getIdentity();
			if (latestIdentity?.user?.id && latestIdentity.user.id !== userId) return null;

			const latestCompany =
				latestIdentity?.user?.id === userId
					? latestIdentity.company_visited
					: currentCompany;
			const updatedUser = enrichUserFromApi(currentUser, userFromApi);
			setUser((previousUser) => ({
				...previousUser,
				user: updatedUser,
				company_visited: latestCompany,
			}));
			persistIdentity(updatedUser, latestCompany);

			return updatedUser;
		})();

		const refreshRequest = { userId, promise: refreshPromise };
		refreshInFlight.current = refreshRequest;
		try {
			return await refreshPromise;
		} finally {
			if (refreshInFlight.current === refreshRequest) refreshInFlight.current = null;
		}
	};

	const refreshCurrentUser = async (userId: string = (user.user as any)?.id) => {
		if (!userId) return null;

		const persistedIdentity = browserSession.getIdentity();
		const currentUser =
			persistedIdentity?.user?.id === userId ? persistedIdentity.user : user.user ?? {};
		const currentCompany =
			persistedIdentity?.user?.id === userId
				? persistedIdentity.company_visited
				: user.company_visited;

		return refreshUserFromApi(userId, currentUser, currentCompany);
	};

	useEffect(() => {
		let mounted = true;

		const hydratePersistedSession = async () => {
			const identity = browserSession.getIdentity();
			const verify = browserSession.getVerify();
			let currentUser = sanitizeUser(identity?.user ?? {});
			let currentCompany = identity?.company_visited ?? null;
			const userId = identity?.user?.id;

			if (mounted) {
				setUser({
					user: currentUser,
					company_visited: currentCompany,
					verifyToken: verify?.verifyToken ?? null,
					confirmationCode: verify?.confirmationCode ?? null,
				});
			}
			persistIdentity(currentUser, currentCompany);

			if (userId && verify?.verifyToken) {
				try {
					const updatedUser = await refreshUserFromApi(userId, currentUser, currentCompany);
					if (!updatedUser || sessionInvalidated.current) {
						if (mounted) setIsHydrating(false);
						return;
					}
					if (mounted) {
						currentUser = updatedUser;
					}
				} catch {
					console.error('Failed to refresh the persisted user profile.');
				}
			}

			const legacyCompanyId = identity?.user?.company_id;
			if (mounted && !currentCompany?.id && legacyCompanyId) {
				try {
					const companyResponse = await getCompanyId(legacyCompanyId);
					const migratedCompany =
						companyResponse?.Item ??
						companyResponse?.company?.Item ??
						companyResponse?.company ??
						companyResponse ??
						null;

					if (mounted && migratedCompany) {
						currentCompany = migratedCompany;
						setUser((previousUser) => ({ ...previousUser, company_visited: currentCompany }));
						persistIdentity(currentUser, currentCompany);
					}
				} catch {
					console.error('Failed to migrate the persisted company.');
				}
			}

			if (mounted) setIsHydrating(false);
		};

		void hydratePersistedSession().catch(() => {
			if (mounted) setIsHydrating(false);
		});
		return () => {
			mounted = false;
		};
	}, []);

	const persistVerifyData = (verifyData: { verifyToken?: any; confirmationCode?: any }) => {
		browserSession.setVerify({
			verifyToken: verifyData?.verifyToken ?? null,
			confirmationCode: verifyData?.confirmationCode ?? null,
		});
	};

	const saveDataSignUpOnStorage = async (response: any) => {
		sessionInvalidated.current = false;

		const normalizedUserCompany = normalizeUserCompany(
			response,
			(user.user as any)?.user_company ?? null
		);
		const normalizedUser: any = sanitizeUser(response ?? {});
		if (normalizedUserCompany) {
			normalizedUser.user_company = normalizedUserCompany;
		}
		const normalizedCompany = normalizeCompanyVisited(response) ?? user.company_visited ?? null;

		setUser((prevUser) => ({
			...prevUser,
			user: normalizedUser,
			company_visited: normalizedCompany,
		}));

		persistIdentity(normalizedUser, normalizedCompany);

		if (normalizedUser?.id) {
			try {
				await refreshUserFromApi(normalizedUser.id, normalizedUser, normalizedCompany);
			} catch (e) {
				console.error('Erro ao buscar grupos do usuário:', e);
			}
		}
	};

	const saveDataVerifyOnStorage = async (response: any) => {
		sessionInvalidated.current = false;

		const nextVerifyToken =
			response?.verifyToken !== undefined ? response.verifyToken : user.verifyToken;

		const nextConfirmationCode =
			response?.confirmationCode !== undefined
				? response.confirmationCode
				: user.confirmationCode;

		const nextUser: any =
			response?.user !== undefined
				? sanitizeUser({ ...(response.user ?? {}) })
				: sanitizeUser({ ...(user.user ?? {}) });
		const nextUserCompany =
			response?.user_company !== undefined
				? response.user_company
				: nextUser.user_company ?? (user.user as any)?.user_company ?? null;
		if (nextUserCompany) {
			nextUser.user_company = nextUserCompany;
		}
		const nextCompanyVisited =
			response?.company_visited !== undefined
				? response.company_visited
				: user.company_visited ?? null;

		setUser((prevUser) => ({
			...prevUser,
			verifyToken: nextVerifyToken,
			confirmationCode: nextConfirmationCode,
			user: nextUser,
			company_visited: nextCompanyVisited,
		}));

		persistVerifyData({
			verifyToken: nextVerifyToken,
			confirmationCode: nextConfirmationCode,
		});

		persistIdentity(nextUser, nextCompanyVisited);

		if (response?.user?.id) {
			try {
				await refreshUserFromApi(response.user.id, nextUser, nextCompanyVisited);
			} catch (e) {
				console.error('Erro ao buscar grupos do usuário:', e);
			}
		}
	};

	const updateCompanyVisited = (companyVisited: any) => {
		const nextCompany = companyVisited ?? null;
		const persistedIdentity = browserSession.getIdentity();
		const currentUser =
			persistedIdentity?.user?.id ? persistedIdentity.user : user.user ?? {};

		persistIdentity(currentUser, nextCompany);
		setUser((prevUser) => ({
			...prevUser,
			company_visited: nextCompany,
		}));
	};

	const updateCurrentUser = (partialUser: Record<string, any>) => {
		setUser((prevUser) => {
			const nextUser = {
				...(prevUser.user ?? {}),
				...(partialUser ?? {}),
			};

			persistIdentity(nextUser, prevUser.company_visited);

			return {
				...prevUser,
				user: nextUser,
			};
		});
	};

	const saveRetryingFlagOnStorage = (response: { isRetrying: boolean }) => {
		browserSession.setRetryingFlag({
			isRetrying: !!response?.isRetrying,
		});
	};

	const deleteRetryingFlagOnStorage = () => {
		browserSession.clearRetryingFlag();
	};

	const SignOut = (
		destiny: string = '/signup',
		options: { message?: string; redirectDelay?: number; toastDuration?: number } = {}
	) => {
		sessionInvalidated.current = true;
		browserSession.clearAllAuthData();
		clearTemporaryStatisticsCaches();
		setUser(initialUserState);

		showToast(options.message ?? 'Sua sessão foi encerrada!', 'info', options.toastDuration ?? 800);

		setTimeout(() => {
			window.location.href = destiny;
		}, options.redirectDelay ?? 1000);
	};

	const signOutSoft = (navigate: Function, redirectTo: string = '/signup') => {
		sessionInvalidated.current = true;
		browserSession.clearAllAuthData();
		clearTemporaryStatisticsCaches();

		setUser(initialUserState);
		navigate(redirectTo);
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				SignOut,
				signOutSoft,
				saveDataSignUpOnStorage,
				updateCompanyVisited,
				saveDataVerifyOnStorage,
				saveRetryingFlagOnStorage,
				deleteRetryingFlagOnStorage,
				updateCurrentUser,
				refreshCurrentUser,
				isHydrating,
			}}
		>
			<LocaleSync user={user} />
			{children}
		</AuthContext.Provider>
	);
};

export function useAuth() {
	const context = useContext(AuthContext);
	return context;
}
